import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import PropTypes from "prop-types";
import { DeleteIcon, MedicalNotesIcon } from "../../../Icons";
import { displayDateUS } from "../../../utils/dateUtils";
import useConfirm from "../../../hooks/useConfirm";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { canDeletePatientNote } from "../patientPermissions";
import { patientSectionTones, PATIENT_THEME } from "../patientTheme";
import {
  PATIENT_NOTE_TYPE_ASSESSMENT,
  PATIENT_NOTE_TYPE_DEMOGRAPHICS,
  extractPatientNotes,
} from "../patientNote.shared";
import {
  getPatientNotes,
  createPatientNote,
  deletePatientNote,
  getAssessmentNotes,
  createAssessmentNote,
  deleteAssessmentNote,
} from "../../../services/patientService";

const noteDateLabel = (note) =>
  displayDateUS(note.noteDate || note.createdAt) || "—";

const NOTES_TONE = patientSectionTones(["notes"]).notes;

const NOTE_API = {
  [PATIENT_NOTE_TYPE_DEMOGRAPHICS]: {
    list: getPatientNotes,
    create: createPatientNote,
    delete: deletePatientNote,
    listParams: { type: PATIENT_NOTE_TYPE_DEMOGRAPHICS },
    createPayload: (description) => ({
      type: PATIENT_NOTE_TYPE_DEMOGRAPHICS,
      description,
    }),
  },
  [PATIENT_NOTE_TYPE_ASSESSMENT]: {
    list: getAssessmentNotes,
    create: createAssessmentNote,
    delete: deleteAssessmentNote,
    listParams: { type: PATIENT_NOTE_TYPE_ASSESSMENT },
    createPayload: (description) => ({
      type: PATIENT_NOTE_TYPE_ASSESSMENT,
      description,
    }),
  },
};

const PatientNotesSection = ({
  patientId,
  canEditNotes,
  noteType = PATIENT_NOTE_TYPE_DEMOGRAPHICS,
  onSaved,
}) => {
  const { role, userId } = useCurrentUser();
  const { confirm, confirmDialog } = useConfirm();
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [notesSaving, setNotesSaving] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  const api = useMemo(
    () => NOTE_API[noteType] || NOTE_API[PATIENT_NOTE_TYPE_DEMOGRAPHICS],
    [noteType],
  );

  const loadNotes = useCallback(async () => {
    if (!patientId) return;
    setNotesLoading(true);
    try {
      const res = await api.list(patientId, {
        limit: 50,
        page: 1,
        ...api.listParams,
      });
      setNotes(extractPatientNotes(res));
    } catch (err) {
      toast.error(err.message || "Failed to load notes");
    } finally {
      setNotesLoading(false);
    }
  }, [patientId, api]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleAddNote = async () => {
    if (!noteDraft.trim()) {
      toast.error("Note description is required");
      return;
    }
    setNotesSaving(true);
    try {
      await api.create(patientId, api.createPayload(noteDraft.trim()));
      toast.success("Note added");
      setNoteDraft("");
      await loadNotes();
      onSaved?.();
    } catch (err) {
      toast.error(err.message || "Failed to add note");
    } finally {
      setNotesSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    const ok = await confirm({
      title: "Delete note?",
      message: "This note will be permanently removed. This cannot be undone.",
    });
    if (!ok) return;
    try {
      await api.delete(patientId, noteId);
      toast.success("Note deleted");
      await loadNotes();
      onSaved?.();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  return (
    <>
      <section
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${NOTES_TONE.wrap}`}
      >
        <div
          className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b ${NOTES_TONE.head}`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`w-0.5 h-4 rounded-full shrink-0 ${NOTES_TONE.bar}`}
              aria-hidden
            />
            <span className={`shrink-0 ${NOTES_TONE.icon}`}>
              <MedicalNotesIcon size={14} />
            </span>
            <h3
              className={`text-xs font-semibold uppercase tracking-wide ${NOTES_TONE.title}`}
            >
              Notes
            </h3>
          </div>
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-md ${NOTES_TONE.badge}`}
          >
            {notes.length}
          </span>
        </div>

        {canEditNotes ? (
          <div className="flex flex-col sm:flex-row items-start gap-2 px-4 py-3 border-b border-slate-100 bg-white">
            <textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Write a note..."
              rows={2}
              className={`flex-1 min-w-0 w-full text-sm border border-slate-200 rounded-md bg-white outline-none px-2.5 py-1.5 resize-none ${PATIENT_THEME.fieldFocus}`}
            />
            <button
              type="button"
              disabled={notesSaving}
              onClick={handleAddNote}
              className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto shrink-0 border ${PATIENT_THEME.button.outline}`}
            >
              {notesSaving ? (
                <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : null}
              Add Note
            </button>
          </div>
        ) : (
          <p className="px-4 py-1.5 text-[11px] text-amber-800 bg-amber-50 border-b border-amber-100">
            Notes are read-only for your role
          </p>
        )}

        <div className="max-h-80 overflow-y-auto">
          {notesLoading ? (
            <p className="px-4 py-5 text-center text-sm text-slate-400 animate-pulse">
              Loading notes...
            </p>
          ) : notes.length === 0 ? (
            <p className="px-4 py-5 text-center text-sm text-slate-500">
              No notes yet
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {notes.map((note) => (
                <li key={note.id} className="px-4 py-2.5 hover:bg-slate-50/70">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">
                        {note.description}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {noteDateLabel(note)}
                        {" · "}
                        {note.createdByName || "Unknown"}
                        {note.createdByRole ? ` (${note.createdByRole})` : ""}
                      </p>
                    </div>
                    {canEditNotes &&
                      canDeletePatientNote(role, note.createdBy, userId) && (
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-50 rounded shrink-0"
                          onClick={() => handleDeleteNote(note.id)}
                          title="Delete note"
                        >
                          <DeleteIcon size={14} />
                        </button>
                      )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
      {confirmDialog}
    </>
  );
};

PatientNotesSection.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  canEditNotes: PropTypes.bool.isRequired,
  noteType: PropTypes.oneOf([
    PATIENT_NOTE_TYPE_DEMOGRAPHICS,
    PATIENT_NOTE_TYPE_ASSESSMENT,
  ]),
  onSaved: PropTypes.func,
};

export default PatientNotesSection;
